import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Alert, Paper, MenuItem } from '@mui/material';
import { useAuth } from '../AuthContext';

export default function ProfileSection() {
  const { token, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ weight_kg: '', height_cm: '', phone_number: '', default_technique: 'classic' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch profile');
        const data = await res.json();
        setProfile(data);
        setForm({
          weight_kg: data.weight_kg || '',
          height_cm: data.height_cm || '',
          phone_number: data.phone_number || '',
          default_technique: data.default_technique || 'classic',
        });
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [token]);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const res = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to update profile');
      const data = await res.json();
      setProfile(data);
      // Update user in localStorage to include default_technique
      if (user) {
        const updatedUser = { ...user, default_technique: data.default_technique };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        // Reload to update AuthContext with new default_technique
        setTimeout(() => window.location.reload(), 1000);
      }
      setSuccess(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, px: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Profile</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Profile updated!</Alert>}
        <TextField label="Name" disabled value={profile?.name || ''} fullWidth margin="normal" slotProps={{textField: {inputProps: { readOnly: true }}}} />
        <TextField label="Email" disabled value={profile?.email || ''} fullWidth margin="normal" slotProps={{textField: {inputProps: { readOnly: true }}}} />
        <TextField label="Role" disabled value={profile?.role || ''} fullWidth margin="normal" slotProps={{textField: {inputProps: { readOnly: true }}}} />
        <TextField label="Phone Number" name="phone_number" value={form.phone_number} onChange={handleChange} fullWidth margin="normal" />
        <TextField label="Weight (kg)" name="weight_kg" value={form.weight_kg} onChange={handleChange} type="number" fullWidth margin="normal" />
        <TextField label="Height (cm)" name="height_cm" value={form.height_cm} onChange={handleChange} type="number" fullWidth margin="normal" />
        <TextField 
          select 
          label="Default Technique" 
          name="default_technique" 
          value={form.default_technique} 
          onChange={handleChange} 
          fullWidth 
          margin="normal"
          helperText="This will be the default technique when adding skis, tests, and races"
        >
          <MenuItem value="classic">Classic</MenuItem>
          <MenuItem value="skate">Skate</MenuItem>
        </TextField>
        <Button onClick={handleSave} variant="contained" sx={{ mt: 2 }} disabled={saving}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </Paper>
    </Box>
  );
} 